import { GraphWithProjects } from '../api/api.ts';
import classNames from 'classnames';
import { borderLeftColorClassesForColor } from '../utils/Colors.ts';
import { useContext } from 'react';
import { LanguageContext } from '@/common/Language.tsx';

// NewGraphText component for text-based project exploration
export default function NewGraphText({
  enrichedGraph,
  selectedIndexes,
  setSelectedIndexes,
}: {
  enrichedGraph: GraphWithProjects;
  selectedIndexes: { category: number; subcategory: number; project: number };
  setSelectedIndexes: (indexes: {
    category: number;
    subcategory: number;
    project: number;
  }) => void;
}) {
  const { t } = useContext(LanguageContext);
  return (
    <div
      className={classNames(
        'h-full',
        'overflow-y-auto',
        'p-4',
        'pt-[60px]',
        'bg-gray-100'
      )}
    >
      <h1
        className={classNames('text-2xl', 'font-bold', 'mb-6', 'text-center')}
      >
        Climate and Water Under Change
      </h1>

      <div className={classNames('space-y-6')}>
        {enrichedGraph.categories.map((category, categoryIndex) => (
          <div
            key={category.id}
            className={classNames(
              'border',
              'rounded-lg',
              'p-4',
              'bg-gray-100',
              'shadow-sm'
            )}
          >
            {/* Category Header */}
            <div
              className={classNames(
                'cursor-pointer',
                'p-3',
                'rounded-md',
                'mb-4',
                'transition-colors',
                'border-l-4',
                categoryIndex === selectedIndexes.category
                  ? [
                      'bg-gray-200',
                      ...borderLeftColorClassesForColor(category.color),
                    ]
                  : ['hover:bg-gray-50', 'border-l-gray-300']
              )}
              onClick={() =>
                setSelectedIndexes({
                  category: categoryIndex,
                  subcategory: -1,
                  project: -1,
                })
              }
            >
              <h2 className={classNames('text-xl', 'font-semibold', 'mb-2')}>
                {t(category.label, category.labelDe)}
              </h2>
              <p className={classNames('text-gray-600', 'text-sm')}>
                {t(category.description, category.descriptionDe)}
              </p>
            </div>

            {/* Subcategories */}
            <div className={classNames('ml-4', 'space-y-3')}>
              {category.subcategories.map((subcategory, subcategoryIndex) => (
                <div key={subcategory.id}>
                  {/* Subcategory Header */}
                  <div
                    className={classNames(
                      'cursor-pointer',
                      'p-2',
                      'rounded-md',
                      'transition-colors',
                      categoryIndex === selectedIndexes.category &&
                        subcategoryIndex === selectedIndexes.subcategory
                        ? 'bg-gray-200'
                        : 'hover:bg-gray-50'
                    )}
                    onClick={() =>
                      setSelectedIndexes({
                        category: categoryIndex,
                        subcategory: subcategoryIndex,
                        project: -1,
                      })
                    }
                  >
                    <h3 className={classNames('font-medium', 'mb-1')}>
                      {t(subcategory.label, subcategory.labelDe)}
                    </h3>
                    <p className={classNames('text-gray-500', 'text-xs')}>
                      {t(subcategory.description, subcategory.descriptionDe)}
                    </p>
                  </div>

                  {/* Projects */}
                  <div className={classNames('ml-4', 'mt-2', 'space-y-1')}>
                    {subcategory.projects.map((project, projectIndex) => (
                      <div
                        key={project.id}
                        className={classNames(
                          'cursor-pointer',
                          'p-2',
                          'rounded-sm',
                          'text-sm',
                          'transition-colors',
                          categoryIndex === selectedIndexes.category &&
                            subcategoryIndex === selectedIndexes.subcategory &&
                            projectIndex === selectedIndexes.project
                            ? 'bg-gray-300'
                            : 'hover:bg-gray-50'
                        )}
                        onClick={() =>
                          setSelectedIndexes({
                            category: categoryIndex,
                            subcategory: subcategoryIndex,
                            project: projectIndex,
                          })
                        }
                      >
                        <div className={classNames('font-medium')}>
                          {t(project.shortName, project.shortNameDe)}
                        </div>
                        <div
                          className={classNames(
                            'text-gray-500',
                            'text-xs',
                            'mt-1'
                          )}
                        >
                          {t(project.name, project.nameDe)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
